import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IonIcon } from '@ionic/react';
import { arrowBackOutline } from 'ionicons/icons';
import MarkdownRenderer from '../../help/components/MarkdownRenderer';
import { getArticleBySlug } from '../../help/content';
import ProCard from '../../components/pro/ProCard';
import ProListRow from '../../components/pro/ProListRow';
import './ProAccount.css';

const ProHelpArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('pro');

  const article = useMemo(() => (slug ? getArticleBySlug(slug) : undefined), [slug]);

  // The app bar now shows the article title, so strip the leading H1 from the
  // markdown body to avoid rendering the title twice.
  const body = useMemo(
    () => (article ? article.content.replace(/^\s*#\s+.*(?:\r?\n)+/, '') : ''),
    [article],
  );

  // The shell's IonContent is shared across routes — when the user scrolls
  // the Help list and taps an article, Ionic keeps that scroll position
  // and the article opens mid-page. Reset to the top whenever the slug
  // changes so every article view starts at the beginning.
  useEffect(() => {
    const ionContent = document.querySelector('ion-content') as
      | (HTMLElement & { scrollToTop?: (duration?: number) => Promise<void> })
      | null;
    ionContent?.scrollToTop?.(0);
  }, [slug]);

  if (!article) {
    return (
      <div className="pro-account max-w-3xl mx-auto px-4 pt-6 pb-8">
        <ProCard>
          <ProListRow title={t('help.articleNotFound')} />
        </ProCard>
        <button
          type="button"
          onClick={() => navigate('/pro/help')}
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary-700 text-white font-semibold text-sm transition-colors active:bg-primary-800"
        >
          <IonIcon icon={arrowBackOutline} className="text-lg" />
          {t('help.backToHelp')}
        </button>
      </div>
    );
  }

  return (
    <div className="pro-account max-w-3xl mx-auto px-4 pt-4 pb-10">
      <ProCard padded contentClassName="px-[18px] py-4">
        <MarkdownRenderer content={body} />
      </ProCard>
    </div>
  );
};

export default ProHelpArticlePage;
